import React, {
  HTMLAttributes,
  ReactNode,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./future-wrap.module.css";
import { FromToEffect } from "../from-to-effect/from-to-effect";
import useMeasure from "react-use-measure";
import ShuffleWrap, {
  ShuffleWrapHandle,
  ShuffleWrapProps,
} from "../shuffle-wrap/shuffle-wrap";
import { gsap } from "gsap";
import { useGsap } from "../use-gsap/use-gsap";

/* eslint-disable-next-line */
export interface FutureWrapProps extends HTMLAttributes<HTMLElement> {
  parallax?: boolean;
  chamfer?: string;
  strokeWidth?: string;
  cornerHighlight?: string;
  cornerHighlightStrokeWidth?: string;
  shuffleEffect?: ShuffleWrapProps;
  admissionEffect?: FutureWrapAnimationProps;
  wrapClassName?: string;
  fromToDeps?: any[];
}

interface FutureWrapAnimationProps {
  animation?: {
    duration?: number;
    ease?: string;
    delay?: number;
  };

  scrollTrigger?: ScrollTrigger.Vars;
  onComplete?: () => void;
  onReverseComplete?: () => void;
}

export interface FutureWrapHandle {
  getTimeLine: () => GSAPTimeline | undefined;
}

export const FutureWrap = forwardRef<FutureWrapHandle, FutureWrapProps>(
  function FutureWrap(props, ref) {
    const {
      parallax = false,
      chamfer = "30",
      strokeWidth = "1",
      cornerHighlight = "0",
      cornerHighlightStrokeWidth = "1",
      shuffleEffect,
      admissionEffect,
      wrapClassName,
      ...reset
    } = props;
    const [ctnRef, { width, height }] = useMeasure();
    const wrapRef = useRef<HTMLDivElement>(null);
    const {
      topLeft: chamferTopLeft,
      topRight: chamferTopRight,
      bottomRight: chamferBottomRight,
      bottomLeft: chamferBottomLeft,
    } = getRectProperty(chamfer);
    const {
      topLeft: strokeWidthTop,
      topRight: strokeWidthRight,
      bottomRight: strokeWidthBottom,
      bottomLeft: strokeWidthLeft,
    } = getRectProperty(strokeWidth);

    const shuffleRef = useRef<ShuffleWrapHandle>(null);
    const timeline = useRef<GSAPTimeline>();

    useImperativeHandle(
      ref,
      () => {
        return {
          getTimeLine: () => {
            if (timeline.current) {
              return timeline.current;
            }
          },
        };
      },
      [timeline.current]
    );

    useGsap(() => {
      if (admissionEffect) {
        const GSAPTimeline = gsap
          .timeline({
            defaults: admissionEffect?.animation,
            paused: true,
            onStart: () => {
              // console.log("start");
            },
            onComplete: (self) => {
              // console.log("Complete");
              if (admissionEffect?.onComplete) admissionEffect.onComplete();
            },
            onReverseComplete: () => {
              // console.log("ReverseComplete");
              if (admissionEffect?.onReverseComplete)
                admissionEffect.onReverseComplete();
            },
          })
          .fromTo(
            wrapRef.current,
            {
              "--future-wrap-corner-highlight-width": "0%",
              "--future-wrap-corner-highlight-height": "0%",
              "--future-wrap-corner-opacity": 0,
              visibility: "hidden",
            },
            {
              "--future-wrap-corner-highlight-width": "100%",
              "--future-wrap-corner-highlight-height": "100%",
              "--future-wrap-corner-opacity": 1,
              visibility: "visible",
            }
          );

        const shuffleEffectTimeLine = shuffleRef.current?.getTimeLine();
        if (shuffleEffectTimeLine) {
          GSAPTimeline.add(shuffleEffectTimeLine);

          shuffleEffectTimeLine.resume();
        }

        timeline.current = GSAPTimeline;
      }
    },[]);

    function render() {
      return (
        <div className={`${styles.wrap} ${wrapClassName}`} ref={wrapRef}>
          <div
            className={`${styles.ctn} ${parallax ? styles.parallax : ""} `}
            ref={ctnRef}
            style={
              {
                "--future-wrap-width": `${width}px`,
                "--future-wrap-height": `${height}px`,
                // "--future-chamfer-x": `${(chamfer / width) * 100}%`,
                // "--future-chamfer-y": `${(chamfer / height) * 100}%`,
                "--future-chamfer-top-left-x": `${
                  (chamferTopLeft / width) * 100
                }%`,
                "--future-chamfer-top-right-x": `${
                  (chamferTopRight / width) * 100
                }%`,
                "--future-chamfer-top-right-y": `${
                  (chamferTopRight / height) * 100
                }%`,
                "--future-chamfer-bottom-right-y": `${
                  (chamferBottomRight / height) * 100
                }%`,
                "--future-chamfer-bottom-right-x": `${
                  (chamferBottomRight / width) * 100
                }%`,
                "--future-chamfer-bottom-left-x": `${
                  (chamferBottomLeft / width) * 100
                }%`,
                "--future-chamfer-bottom-left-y": `${
                  (chamferBottomLeft / height) * 100
                }%`,
                "--future-chamfer-top-left-y": `${
                  (chamferTopLeft / height) * 100
                }%`,
                ...reset.style,
              } as React.CSSProperties
            }
          >
            <div className={styles.putElement}>
              <div {...reset}>{reset.children}</div>
            </div>
            <div
              className={`${styles.inside}`}
              style={{
                top: `${strokeWidthTop}px`,
                right: `${strokeWidthRight}px`,
                bottom: `${strokeWidthBottom}px`,
                left: `${strokeWidthLeft}px`,
              }}
            >
              {shuffleEffect ? (
                <ShuffleWrap {...shuffleEffect} ref={shuffleRef} {...reset}>
                  {parallax ? (
                    <FromToEffect
                      wrap={false}
                      scrollTrigger={{
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 1,
                      }}
                      parallax
                    >
                      {reset.children}
                    </FromToEffect>
                  ) : (
                    reset.children
                  )}
                </ShuffleWrap>
              ) : (
                <div {...reset}>
                  {parallax ? (
                    <FromToEffect
                      wrap={false}
                      scrollTrigger={{
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 1,
                      }}
                      parallax
                    >
                      {reset.children}
                    </FromToEffect>
                  ) : (
                    reset.children
                  )}
                </div>
              )}
            </div>
          </div>

          <CornerHighlight
            cornerHighlight={cornerHighlight}
            cornerHighlightStrokeWidth={cornerHighlightStrokeWidth}
          />
        </div>
      );
    }
    return admissionEffect?.scrollTrigger ? (
      <FromToEffect
        deps={props.fromToDeps}
        wrap={false}
        scrollTrigger={{
          ...admissionEffect.scrollTrigger,
          onEnter: (self) => {
            // console.log(admissionEffect.scrollTrigger);
            timeline.current?.play();
            if (admissionEffect.scrollTrigger?.onEnter)
              admissionEffect.scrollTrigger?.onEnter(self);
          },
        }}
      >
        {render()}
      </FromToEffect>
    ) : (
      render()
    );
  }
);

interface CornerHighlightProps {
  cornerHighlight: string;
  cornerHighlightStrokeWidth: string;
}

function CornerHighlight(props: CornerHighlightProps) {
  const { cornerHighlight = "0", cornerHighlightStrokeWidth = "1" } = props;
  const {
    topLeft: cornerHighlightTopLeft,
    topRight: cornerHighlightTopRight,
    bottomRight: cornerHighlightBottomRight,
    bottomLeft: cornerHighlightBottomLeft,
  } = getRectProperty(cornerHighlight);

  const {
    topLeft: cornerHighlightStrokeWidthTopLeft,
    topRight: cornerHighlightStrokeWidthTopRight,
    bottomRight: cornerHighlightStrokeWidthBottomRight,
    bottomLeft: cornerHighlightStrokeWidthBottomLeft,
  } = getRectProperty(cornerHighlightStrokeWidth);

  return (
    <div className={styles.cornerHighlightBox}>
      <div
        className={styles.cornerHighlightTopLeft}
        style={
          {
            "--future-corner-highlight": `${cornerHighlightTopLeft}px`,
            "--future-corner-highlight-stroke-width": `${cornerHighlightStrokeWidthTopLeft}px`,
            top: 0,
            left: 0,
          } as React.CSSProperties
        }
      >
        <div className={styles.cornerHighlightX}></div>
        <div className={styles.cornerHighlightY}></div>
      </div>

      <div
        className={styles.cornerHighlightTopRight}
        style={
          {
            "--future-corner-highlight": `${cornerHighlightTopRight}px`,
            "--future-corner-highlight-stroke-width": `${cornerHighlightStrokeWidthTopRight}px`,
            top: 0,
            right: 0,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.cornerHighlightX}
          style={{
            right: 0,
          }}
        ></div>
        <div
          className={styles.cornerHighlightY}
          style={{
            right: 0,
          }}
        ></div>
      </div>

      <div
        className={styles.cornerHighlightBottomRight}
        style={
          {
            "--future-corner-highlight": `${cornerHighlightBottomRight}px`,
            "--future-corner-highlight-stroke-width": `${cornerHighlightStrokeWidthBottomRight}px`,
            bottom: 0,
            right: 0,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.cornerHighlightX}
          style={{
            bottom: 0,
            right: 0,
          }}
        ></div>
        <div
          className={styles.cornerHighlightY}
          style={{
            bottom: 0,
            right: 0,
          }}
        ></div>
      </div>

      <div
        className={styles.cornerHighlightBottomLeft}
        style={
          {
            "--future-corner-highlight": `${cornerHighlightBottomLeft}px`,
            "--future-corner-highlight-stroke-width": `${cornerHighlightStrokeWidthBottomLeft}px`,
            bottom: 0,
            left: 0,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.cornerHighlightX}
          style={{
            left: 0,
            bottom: 0,
          }}
        ></div>
        <div
          className={styles.cornerHighlightY}
          style={{
            left: 0,
            bottom: 0,
          }}
        ></div>
      </div>
    </div>
  );
}

type RectProperty = {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
};

function getRectProperty(input: string): RectProperty {
  const values = input.split(" ");

  if (values.length === 1) {
    const [radius] = values;
    return {
      topLeft: Number(radius),
      topRight: Number(radius),
      bottomRight: Number(radius),
      bottomLeft: Number(radius),
    };
  } else if (values.length === 2) {
    const [topLeftBottomRight, topRightBottomLeft] = values;
    return {
      topLeft: Number(topLeftBottomRight),
      topRight: Number(topRightBottomLeft),
      bottomRight: Number(topLeftBottomRight),
      bottomLeft: Number(topRightBottomLeft),
    };
  } else if (values.length === 3) {
    const [topLeft, topRightBottomLeft, bottomRight] = values;
    return {
      topLeft: Number(topLeft),
      topRight: Number(topRightBottomLeft),
      bottomRight: Number(bottomRight),
      bottomLeft: Number(topRightBottomLeft),
    };
  } else if (values.length === 4) {
    const [topLeft, topRight, bottomRight, bottomLeft] = values;
    return {
      topLeft: Number(topLeft),
      topRight: Number(topRight),
      bottomRight: Number(bottomRight),
      bottomLeft: Number(bottomLeft),
    };
  } else {
    throw new Error(
      "Invalid input. Please provide one, two, three, or four values separated by spaces."
    );
  }
}

export default FutureWrap;
